import { useIsMobile } from "@elit-hair/elit-ui";
import { Sling as Hamburger } from "hamburger-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DarwinSidebarToggle } from "@/components/ai-chat/darwin-sidebar-toggle";
import { MobileMenu } from "@/components/mobile-menu/mobile-menu";

import { HeaderCallDialer } from "./call-dialer";
import { HeaderDialerPopover } from "./dialer-popover";
import { HeaderKpi } from "./kpi";
import { HeaderLastCallPopover } from "./last-call-popover";
import { HeaderNotifications } from "./notifications";
import { HeaderReportIncident } from "./report-incident";
import { HeaderStatusDropdown } from "./status-dropdown";
import { HeaderProps } from "./types/header.types";
import { usePageTitle } from "./use-page-title";

/**
 * Render the application header with page title and responsive mobile UI.
 *
 * Renders the page title, KPI area (inline on md+), notifications,
 * last-call and dialer popovers (desktop), an optional Darwin sidebar toggle,
 * a mobile call dialer (mobile only), and a hamburger-controlled mobile menu overlay.
 *
 * @param onToggleRightSidebar - Handler invoked when the Darwin button is clicked
 * @param showRightSidebarToggle - When true, show the Darwin sidebar toggle
 * @param isRightSidebarOpen - Whether the Darwin sidebar is currently open
 * @returns The header element containing navigation and control components
 */
export function Header({
    onToggleRightSidebar,
    showRightSidebarToggle = false,
    isRightSidebarOpen = false,
}: HeaderProps) {
    const { t } = useTranslation();
    const { title: pageTitle, translate: translatePageTitle } = usePageTitle();
    const isMobile = useIsMobile();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <header className="sticky top-0 z-20 w-full bg-background border-b rounded-t-lg border-border/50">
            <div className="flex flex-col">
                <div className="flex justify-between items-center w-full h-14 px-3">
                    {/* Left: Title */}
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <h2 className="font-semibold text-xl truncate">
                            {translatePageTitle ? t(pageTitle) : pageTitle}
                        </h2>
                    </div>

                    {/* Right: Controls */}
                    <div className="flex items-center gap-2 shrink-0">
                        {/* KPI inline for md+ */}
                        <div className="hidden md:flex items-center gap-1" data-tour="kpi-area">
                            <HeaderKpi />
                        </div>

                        {/* Report Incident */}
                        <HeaderReportIncident />

                        {/* Notifications */}
                        <HeaderNotifications />

                        {/* Status stays in header on mobile (left sidebar is hidden) */}
                        {isMobile && <HeaderStatusDropdown />}

                        {!isMobile && (
                            <>
                                <HeaderLastCallPopover />
                                <HeaderDialerPopover />
                            </>
                        )}

                        {showRightSidebarToggle && (
                            <DarwinSidebarToggle isOpen={isRightSidebarOpen} onToggle={onToggleRightSidebar} />
                        )}

                        {/* Mobile Dialer */}
                        {isMobile && <HeaderCallDialer />}

                        {/* Hamburger for mobile */}
                        {isMobile && (
                            <div className="ml-1">
                                <Hamburger toggled={mobileMenuOpen} toggle={setMobileMenuOpen} size={20} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Menu overlay */}
            {isMobile && <MobileMenu open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />}
        </header>
    );
}
